import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import React, { useEffect, useState } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import useToastAlert from "@/hooks/useToastAlert";
import { PronunciationLexicon } from "../types";
import useTeams from "@/hooks/useTeams";

interface Props {
  show: boolean;
  onClose(): void;
  onSave(lexicon: PronunciationLexicon): void;
  existingLexicon?: PronunciationLexicon;
}

export default function PronunciationLexiconModal(props: Props) {
  const api = useApi();
  const { teams } = useTeams();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const toastAlert = useToastAlert();

  const [formData, setFormData] = useState<Partial<PronunciationLexicon>>({
    correctedText: "",
    misrecognitions: "",
    type: "Both",
    isActive: true,
    order: 0,
    notes: "",
    teamId: undefined
  });

  useEffect(() => {
    if (props.existingLexicon) {
      setFormData(props.existingLexicon);
    } else {
      setFormData({
        correctedText: "",
        misrecognitions: "",
        type: "Both",
        isActive: true,
        order: 0,
        notes: "",
        teamId: undefined
      });
    }
  }, [props.existingLexicon, props.show]);

  async function handleSubmit() {
    if (!formData.correctedText || !formData.misrecognitions) {
      setError("Written word or phrase and Spoken Pronunciation are required.");
      return;
    }

    setSending(true);
    setError("");

    try {
      const payload = {
        correctedText: formData.correctedText,
        misrecognitions: formData.misrecognitions,
        type: formData.type,
        isActive: formData.isActive,
        order: formData.order || 0,
        notes: formData.notes || "",
        teamId: formData.teamId || null
      };

      let response;
      if (props.existingLexicon) {
        response = await api.messaging.put(
          `PronunciationLexicons/${props.existingLexicon.id}`,
          payload
        );
      } else {
        response = await api.messaging.post("PronunciationLexicons", payload);
      }

      if (response.ok) {
        const savedLexicon = await response.json();
        props.onSave(savedLexicon);
        toastAlert.displayToast(
          Severity.Success,
          props.existingLexicon
            ? "Pronunciation correction updated successfully."
            : "Pronunciation correction created successfully."
        );
        props.onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.title || "Failed to save pronunciation correction.");
      }
    } catch (e) {
      setError("An error occurred while saving.");
      console.error(e);
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal show={props.show} maxWidth="max-w-3xl">
      <ModalHeader title="Pronunciation Rules" onClose={props.onClose} />
      <ModalBody>
        <div className="mb-4 text-sm text-gray-600">
          Teach the AI voice agent how to pronounce words or names that are
          spelled one way but spoken another during calls. For example, X7aro
          should be pronounced as Zairo.
        </div>
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        <div className="space-y-4">
          <div style={{ display: "none" }}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order
            </label>
            <input
              type="number"
              min={0}
              max={9999}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.order || 0}
              onChange={(e) =>
                setFormData({ ...formData, order: Number(e.target.value) })
              }
            />
            <p className="text-xs text-gray-500 mt-1">
              Display order (lower numbers first for grid listing display order
              only)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Written word or phrase *
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="X7aro, X-Zaro, X7-aro"
              rows={3}
              value={formData.misrecognitions || ""}
              onChange={(e) =>
                setFormData({ ...formData, misrecognitions: e.target.value })
              }
              maxLength={2048}
            />
            <p className="text-xs text-gray-500 mt-1">
              Written versions used in conversations or training data. Add
              multiple spellings, separated by commas.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Spoken Pronunciation *
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Zairo, Zy-roh"
              value={formData.correctedText || ""}
              onChange={(e) =>
                setFormData({ ...formData, correctedText: e.target.value })
              }
              maxLength={256}
            />
            <p className="text-xs text-gray-500 mt-1">
              How the AI voice agent should say the word during calls. Use
              simple phonetic spelling.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.type || "Both"}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
            >
              <option value="Speech">Speech Recognition (Input)</option>
              <option value="TTS">Text-to-Speech (Output)</option>
              <option value="Both">
                Both - Speech Recognition (Input) and Text-to-Speech (Output)
              </option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Team (Optional)
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.teamId || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  teamId: e.target.value ? Number(e.target.value) : undefined
                })
              }
            >
              <option value="">All Teams</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder=""
              rows={2}
              value={formData.notes || ""}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              maxLength={512}
            />
            <p className="text-xs text-gray-500 mt-1">
              Add context for internal reference, such as client-specific terms
              or internal naming.
            </p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              checked={formData.isActive ?? true}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
            />
            <label
              htmlFor="isActive"
              className="ml-2 block text-sm text-gray-700"
            >
              Active
            </label>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-end gap-2">
          <SecondaryBlueButton
            label="Cancel"
            onClick={props.onClose}
            disabled={sending}
          />
          <PrimaryBlueButton
            label={sending ? "Saving..." : "Save"}
            onClick={handleSubmit}
            disabled={sending}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
}
